"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import gsap from "gsap";
import { motion, type PanInfo } from "motion/react";

export interface CardItem {
  imgUrl: string;
  alt?: string;
  linkUrl?: string;
  position?: string;
}

interface SocialCardsProps {
  cards: CardItem[];
  onCardClick?: (index: number) => void;
  isPaused?: boolean;
  autoAdvanceDelay?: number;
}

const MAX_VISIBLE = 7;
const HALF = 3;

const FAN_POSITIONS = [
  { rot: -21, scale: 0.7756, x: -30, y: 7.3, zIndex: 1 },
  { rot: -14, scale: 0.8498, x: -22, y: 4.0, zIndex: 2 },
  { rot: -7,  scale: 0.9346, x: -11, y: 1.3, zIndex: 3 },
  { rot: 0,   scale: 1.0,    x: 0,   y: 0.0, zIndex: 10 },
  { rot: 7,   scale: 0.9346, x: 11,  y: 1.3, zIndex: 3 },
  { rot: 14,  scale: 0.8498, x: 22,  y: 4.0, zIndex: 2 },
  { rot: 21,  scale: 0.7756, x: 30,  y: 7.3, zIndex: 1 },
];

// --- TUNING CONSTANTS ---
const DRAG_THRESHOLD = 5;
const DRAG_SENSITIVITY = 250;
const DISTANCE_DIVISOR = 200;
const VELOCITY_DIVISOR = 800;

function getResponsiveMultiplier(width: number) {
  if (width < 480) return 0.28;
  if (width < 640) return 0.38;
  if (width < 768) return 0.5;
  if (width < 1024) return 0.75;
  return 1.0;
}

function getHeightMultiplier(width: number) {
  let idealPx: number;
  if (width < 480) idealPx = 22 * 16;
  else if (width < 640) idealPx = 26 * 16;
  else if (width < 768) idealPx = 28 * 16;
  else if (width < 1024) idealPx = 34 * 16;
  else idealPx = 38 * 16;

  if (typeof window === "undefined") return 1;
  const available = window.innerHeight * 0.7;
  if (available >= idealPx) return 1;
  return available / idealPx;
}

function getSlotConfig(totalCards: number, slot: number) {
  if (totalCards >= MAX_VISIBLE) return FAN_POSITIONS[slot];
  const center = totalCards >> 1;
  const distance = totalCards > 1 ? (slot - center) / center : 0;
  const absDistance = Math.abs(distance);
  return {
    rot: distance * 21,
    scale: 1.0 - 0.2244 * absDistance * absDistance,
    x: distance * 30,
    y: absDistance * absDistance * 7.3,
    zIndex: 10 - Math.abs(slot - center),
  };
}

function getCardOffset(index: number, progress: number, total: number) {
  if (total <= 1) return 0;
  let diff = (index - progress) % total;
  while (diff > total / 2) diff -= total;
  while (diff < -total / 2) diff += total;
  return diff;
}

function getFanX(d: number): number {
  const sign = Math.sign(d);
  const absD = Math.abs(d);
  if (absD <= 1) return sign * absD * 11;
  if (absD <= 2) return sign * (11 + (absD - 1) * 11);
  if (absD <= 3) return sign * (22 + (absD - 2) * 8);
  return sign * (30 + (absD - 3) * 8);
}

function getFanY(d: number): number {
  const absD = Math.abs(d);
  if (absD <= 1) return absD * 1.3;
  if (absD <= 2) return 1.3 + (absD - 1) * 2.7;
  if (absD <= 3) return 4.0 + (absD - 2) * 3.3;
  return 7.3 + (absD - 3) * 3.3;
}

function getFanRot(d: number): number {
  return d * 7;
}

function getFanScale(d: number): number {
  const absD = Math.abs(d);
  if (absD <= 1) return 1.0 - absD * 0.0654;
  if (absD <= 2) return 0.9346 - (absD - 1) * 0.0848;
  if (absD <= 3) return 0.8498 - (absD - 2) * 0.0742;
  return Math.max(0.4, 0.7756 - (absD - 3) * 0.2);
}

function getFanOpacity(d: number): number {
  const absD = Math.abs(d);
  if (absD <= 3.2) return 1;
  if (absD <= 3.8) return Math.max(0, 1 - (absD - 3.2) / 0.6);
  return 0;
}

function getFanZIndex(d: number): number {
  const absD = Math.abs(d);
  if (absD < 0.5) return 10;
  if (absD < 1.5) return 3;
  if (absD < 2.5) return 2;
  if (absD <= 3.5) return 1;
  return 0;
}

const ARROW_CLASSES =
  "relative flex items-center justify-center rounded-full border-[1.5px] border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 backdrop-blur-[16px] text-black/40 dark:text-white/55 cursor-pointer shrink-0 z-30 outline-none shadow-[0_4px_20px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:border-black/25 dark:hover:border-white/25 hover:text-black/70 dark:hover:text-white/80 active:opacity-70 transition-colors duration-300 before:content-[''] before:absolute before:inset-[3px] before:rounded-full before:border before:border-black/[0.04] dark:before:border-white/[0.04] before:pointer-events-none";

export default function SocialCards({
  cards,
  onCardClick,
  isPaused = false,
  autoAdvanceDelay = 2000,
}: SocialCardsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAnimating = useRef(false);
  const hasEntered = useRef(false);
  const directionRef = useRef<"left" | "right" | null>(null);
  const prevVisible = useRef<Set<number>>(new Set());

  const isDraggingRef = useRef(false);
  const dragDistanceRef = useRef(0);
  const activeSlotRef = useRef<number | null>(null);
  const leaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoTimerRef = useRef<NodeJS.Timeout | null>(null);

  const totalCards = cards.length;
  const hasMultipleCards = totalCards > 1;
  const needsPagination = totalCards > MAX_VISIBLE;
  const [centerIndex, setCenterIndex] = useState(needsPagination ? HALF : totalCards >> 1);

  const stopAutoAdvance = useCallback(() => {
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const getVisibleMap = useCallback((center: number) => {
    const map = new Map<number, number>();
    if (!needsPagination) {
      const centerSlot = totalCards >> 1;
      cards.forEach((_, i) => {
        const slot = ((i - center + centerSlot) % totalCards + totalCards) % totalCards;
        map.set(i, slot);
      });
      return map;
    }
    for (let slot = 0; slot < MAX_VISIBLE; slot++) {
      map.set(((center + slot - HALF) % totalCards + totalCards) % totalCards, slot);
    }
    return map;
  }, [totalCards, needsPagination, cards]);

  const cycle = useCallback((direction: "left" | "right") => {
    if (isAnimating.current || !hasMultipleCards) return;
    isAnimating.current = true;
    directionRef.current = direction;
    setCenterIndex(prev =>
      direction === "right" ? (prev + 1) % totalCards : (prev - 1 + totalCards) % totalCards
    );
  }, [totalCards, hasMultipleCards]);

  const startAutoAdvance = useCallback((delay = autoAdvanceDelay) => {
    stopAutoAdvance();
    if (!hasMultipleCards || isPaused) return;

    autoTimerRef.current = setTimeout(() => {
      if (isDraggingRef.current || isAnimating.current) {
        startAutoAdvance(500);
        return;
      }
      cycle("right");
    }, delay);
  }, [autoAdvanceDelay, hasMultipleCards, isPaused, cycle, stopAutoAdvance]);

  const goToIndex = (targetIndex: number) => {
    if (isAnimating.current || targetIndex === centerIndex || !hasMultipleCards) return;
    isAnimating.current = true;
    directionRef.current = targetIndex > centerIndex ? "right" : "left";
    setCenterIndex(targetIndex);
  };

  useEffect(() => {
    if (!hasMultipleCards || isPaused) {
      stopAutoAdvance();
      return;
    }

    startAutoAdvance();

    return () => {
      stopAutoAdvance();
    };
  }, [centerIndex, isPaused, hasMultipleCards, startAutoAdvance, stopAutoAdvance]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAutoAdvance();
      } else if (!isPaused && hasMultipleCards) {
        startAutoAdvance();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPaused, hasMultipleCards, startAutoAdvance, stopAutoAdvance]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !totalCards) return;

    const cardElements = Array.from(container.querySelectorAll<HTMLElement>(".fan-card"));
    if (!cardElements.length) return;

    const visibleMap = getVisibleMap(centerIndex);
    const previouslyVisible = prevVisible.current;
    const direction = directionRef.current;
    const isFirstMount = !hasEntered.current;
    const multiplier = getResponsiveMultiplier(window.innerWidth);
    const hMult = getHeightMultiplier(window.innerWidth);
    const slotCount = needsPagination ? MAX_VISIBLE : totalCards;
    const config = (slot: number) => getSlotConfig(slotCount, slot);

    const isTestEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent?.toLowerCase().includes("jsdom");

    if (isTestEnv) {
      cardElements.forEach((card, cardIndex) => {
        const slot = visibleMap.get(cardIndex);
        if (slot !== undefined) {
          const { x, y, rot, scale, zIndex } = config(slot);
          gsap.set(card, {
            x: `${x * multiplier}rem`,
            y: `${y * hMult}rem`,
            rotation: rot,
            scale,
            opacity: 1,
            zIndex,
          });
        } else {
          gsap.set(card, { opacity: 0, scale: 0.5, zIndex: 0 });
        }
      });
      isAnimating.current = false;
      isDraggingRef.current = false;
      hasEntered.current = true;
      return;
    }

    if (isFirstMount) isAnimating.current = true;

    let completedCount = 0;
    const visibleCount = visibleMap.size;
    const onCardDone = () => {
      if (++completedCount >= visibleCount) {
        isAnimating.current = false;
        isDraggingRef.current = false;
        if (isFirstMount) hasEntered.current = true;
      }
    };

    cardElements.forEach((card, cardIndex) => {
      const slot = visibleMap.get(cardIndex);
      const wasVisible = previouslyVisible.has(cardIndex);

      if (slot !== undefined) {
        const { x, y, rot, scale, zIndex } = config(slot);
        const target = {
          x: `${x * multiplier}rem`,
          y: `${y * hMult}rem`,
          rotation: rot,
          scale,
          opacity: 1,
          zIndex,
        };

        if (isFirstMount) {
          gsap.set(card, { x: 0, y: `${12 * hMult}rem`, rotation: 0, scale: 0.5, opacity: 0 });
          gsap.to(card, { ...target, duration: 1.2, ease: "elastic.out(1.05,.78)", delay: 0.2 + slot * 0.06, onComplete: onCardDone });
        } else if (!wasVisible) {
          const enterX = direction === "right" ? 40 : -40;
          gsap.set(card, { x: `${enterX}rem`, y: `${y * hMult}rem`, rotation: direction === "right" ? 30 : -30, scale: 0.5, opacity: 0 });
          gsap.to(card, { ...target, duration: 0.6, ease: "power2.out", onComplete: onCardDone });
        } else {
          gsap.to(card, { ...target, duration: 0.5, ease: "power2.out", onComplete: onCardDone });
        }
      } else if (wasVisible) {
        const exitX = direction === "right" ? -40 : 40;
        gsap.to(card, { x: `${exitX}rem`, opacity: 0, scale: 0.5, rotation: direction === "right" ? -30 : 30, duration: 0.4, ease: "power2.in", zIndex: 0 });
      } else if (isFirstMount) {
        gsap.set(card, { opacity: 0, scale: 0.3, x: 0, y: 0, zIndex: 0 });
      }
    });

    prevVisible.current = new Set(visibleMap.keys());

    // Hover interactions
    const visibleEntries: { el: HTMLElement; slot: number }[] = [];
    cardElements.forEach((el, i) => {
      const slot = visibleMap.get(i);
      if (slot !== undefined) visibleEntries.push({ el, slot });
    });
    visibleEntries.sort((a, b) => a.slot - b.slot);

    const centerSlot = visibleEntries.length >> 1;

    const updateHoverLayout = (hoveredSlot: number | null) => {
      const mult = getResponsiveMultiplier(window.innerWidth);
      const hM = getHeightMultiplier(window.innerWidth);

      visibleEntries.forEach(({ el, slot }) => {
        const base = config(slot);
        let targetX = base.x * mult;
        let targetY = base.y * hM;
        let targetRot = base.rot;
        let targetScale = base.scale;
        let delay = 0;

        if (hoveredSlot !== null) {
          const distance = Math.abs(slot - hoveredSlot);
          delay = distance * 0.02;

          if (slot === hoveredSlot) {
            targetY -= 2.5 * hM;
            targetScale *= 1.08;
          } else {
            const normalized = centerSlot > 0 ? (slot - centerSlot) / centerSlot : 0;
            const pushStrength = 8 * (1 - Math.abs(normalized)) * (1 + 0.2 * Math.max(0, 3 - distance));

            if (slot < hoveredSlot) {
              targetX -= pushStrength * mult;
              targetRot -= 3 / (distance + 1);
            } else {
              targetX += pushStrength * mult;
              targetRot += 3 / (distance + 1);
            }

            if (slot === visibleEntries.length - 1 && hoveredSlot < centerSlot) targetY -= 1 * hM;
            if (slot === 0 && hoveredSlot > centerSlot) targetY -= 1 * hM;
          }
        } else {
          delay = Math.abs(slot - centerSlot) * 0.02;
        }

        gsap.to(el, {
          x: `${targetX}rem`,
          y: `${targetY}rem`,
          rotation: targetRot,
          scale: targetScale,
          duration: 0.5,
          delay,
          ease: "elastic.out(1,.75)",
          overwrite: "auto",
        });
        gsap.set(el, { zIndex: base.zIndex });
      });
    };

    const enterHandlers = visibleEntries.map(({ el, slot }) => {
      const handler = () => {
        if (isAnimating.current || isDraggingRef.current) return;
        startAutoAdvance();
        if (leaveTimerRef.current) {
          clearTimeout(leaveTimerRef.current);
          leaveTimerRef.current = null;
        }
        if (activeSlotRef.current !== slot) {
          activeSlotRef.current = slot;
          updateHoverLayout(slot);
        }
      };
      el.addEventListener("mouseenter", handler);
      return { el, handler };
    });

    const onMouseLeave = () => {
      if (isAnimating.current || isDraggingRef.current) return;
      startAutoAdvance();
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = setTimeout(() => {
        activeSlotRef.current = null;
        updateHoverLayout(null);
      }, 50);
    };
    container.addEventListener("mouseleave", onMouseLeave);

    const onResize = () => {
      if (!isAnimating.current && !isDraggingRef.current) {
        updateHoverLayout(activeSlotRef.current);
      }
    };
    window.addEventListener("resize", onResize);

    return () => {
      enterHandlers.forEach(({ el, handler }) => el.removeEventListener("mouseenter", handler));
      container.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", onResize);
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, [centerIndex, totalCards, getVisibleMap, needsPagination]);

  const handlePanStart = () => {
    stopAutoAdvance();
    dragDistanceRef.current = 0;
    isDraggingRef.current = false;
  };

  const handlePan = (_: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
    if (isAnimating.current && !isDraggingRef.current) return;
    if (!hasMultipleCards) return;

    dragDistanceRef.current = Math.abs(info.offset.x);
    if (dragDistanceRef.current < DRAG_THRESHOLD) return;

    const container = containerRef.current;
    if (!container) return;

    if (!isDraggingRef.current) {
      stopAutoAdvance();
      isDraggingRef.current = true;
      isAnimating.current = true;
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      activeSlotRef.current = null;
    }

    const mult = getResponsiveMultiplier(window.innerWidth);
    const hM = getHeightMultiplier(window.innerWidth);
    const visibleMap = getVisibleMap(centerIndex);
    const slotCount = needsPagination ? MAX_VISIBLE : totalCards;
    const centerSlot = slotCount >> 1;
    const factor = totalCards > 1 && totalCards < MAX_VISIBLE ? 3 / centerSlot : 1;

    const delta = -info.offset.x / DRAG_SENSITIVITY;

    const cardElements = Array.from(container.querySelectorAll<HTMLElement>(".fan-card"));
    cardElements.forEach((el, cardIndex) => {
      const baseSlot = visibleMap.get(cardIndex);
      if (baseSlot !== undefined) {
        const currentSlot = baseSlot - delta;
        const d = (currentSlot - centerSlot) * factor;

        const x = getFanX(d) * mult;
        const y = getFanY(d) * hM;
        const rot = getFanRot(d);
        const scale = getFanScale(d);
        const opacity = getFanOpacity(d);
        const zIndex = getFanZIndex(d);

        gsap.set(el, {
          x: `${x}rem`,
          y: `${y}rem`,
          rotation: rot,
          scale,
          opacity,
          zIndex,
          overwrite: "auto",
        });
      } else {
        gsap.set(el, { opacity: 0, scale: 0.3, zIndex: 0, overwrite: "auto" });
      }
    });
  };

  const handlePanEnd = (_: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
    if (!isDraggingRef.current) {
      isDraggingRef.current = false;
      startAutoAdvance();
      return;
    }

    const container = containerRef.current;
    if (!container) {
      startAutoAdvance();
      return;
    }

    const dragDistance = info.offset.x;
    const velocity = info.velocity.x;

    const distanceShift = -dragDistance / DISTANCE_DIVISOR;
    const velocityShift = -velocity / VELOCITY_DIVISOR;

    let totalShift = Math.round(distanceShift + velocityShift);
    totalShift = Math.max(-3, Math.min(3, totalShift));

    if (totalShift === 0) {
      const mult = getResponsiveMultiplier(window.innerWidth);
      const hM = getHeightMultiplier(window.innerWidth);
      const visibleMap = getVisibleMap(centerIndex);
      const slotCount = needsPagination ? MAX_VISIBLE : totalCards;
      const config = (slot: number) => getSlotConfig(slotCount, slot);
      const cardElements = Array.from(container.querySelectorAll<HTMLElement>(".fan-card"));

      let doneCount = 0;
      const totalToAnimate = visibleMap.size;
      const onSnapDone = () => {
        if (++doneCount >= totalToAnimate) {
          isAnimating.current = false;
          setTimeout(() => {
            isDraggingRef.current = false;
            startAutoAdvance();
          }, 50);
        }
      };

      cardElements.forEach((el, cardIndex) => {
        const slot = visibleMap.get(cardIndex);
        if (slot !== undefined) {
          const base = config(slot);
          gsap.to(el, {
            x: `${base.x * mult}rem`,
            y: `${base.y * hM}rem`,
            rotation: base.rot,
            scale,
            opacity: 1,
            zIndex: base.zIndex,
            duration: 0.45,
            ease: "elastic.out(1, 0.75)",
            overwrite: "auto",
            onComplete: onSnapDone,
          });
        }
      });
    } else {
      directionRef.current = totalShift > 0 ? "right" : "left";
      setCenterIndex((prev) => ((prev + totalShift) % totalCards + totalCards) % totalCards);
      startAutoAdvance();
    }
  };

  if (!totalCards) return null;

  const chevron = (direction: "left" | "right") => (
    <svg className="relative z-[2] w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points={direction === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
    </svg>
  );

  return (
    <section className="flex flex-col items-center w-full py-4 lg:py-8 px-4 md:px-8 relative z-20">
      <div className="flex items-center justify-center w-full max-w-[90rem]">
        <motion.div
          ref={containerRef}
          onPanStart={handlePanStart}
          onPan={handlePan}
          onPanEnd={handlePanEnd}
          className="fan-layout flex relative justify-center items-center w-full max-w-[80rem] cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: "pan-y" }}
        >
          {cards.map((card, index) => {
            const image = (
              <div className="relative w-full h-full overflow-hidden select-none pointer-events-none">
                <img
                  src={card.imgUrl}
                  loading="lazy"
                  alt={card.alt || `Card ${index}`}
                  className="absolute inset-0 w-full h-full object-cover z-10 pointer-events-none select-none"
                  style={{ objectPosition: card.position || "center top" }}
                  draggable={false}
                />
              </div>
            );
            return card.linkUrl ? (
              <a
                key={index}
                href={card.linkUrl}
                target={card.linkUrl.startsWith("http") ? "_blank" : "_self"}
                rel="noopener noreferrer"
                className="fan-card block cursor-pointer select-none"
                style={{ touchAction: "pan-y" }}
                onClick={(e) => {
                  if (isDraggingRef.current || isAnimating.current) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  startAutoAdvance();
                }}
              >
                {image}
              </a>
            ) : (
              <div
                key={index}
                className="fan-card cursor-pointer select-none"
                style={{ touchAction: "pan-y" }}
                onClick={() => {
                  if (isDraggingRef.current || isAnimating.current) return;
                  startAutoAdvance();
                  onCardClick?.(index);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (!isDraggingRef.current && !isAnimating.current) {
                      startAutoAdvance();
                      onCardClick?.(index);
                    }
                  }
                }}
                aria-label={card.alt ? `Open ${card.alt} full size` : `View card ${index + 1} full size`}
              >
                {image}
              </div>
            );
          })}
        </motion.div>
      </div>

      {hasMultipleCards && (
        <div className="flex items-center justify-center gap-4 mt-4 md:mt-6 z-30">
          <button
            className={`${ARROW_CLASSES} w-10 h-10 md:w-12 md:h-12`}
            onClick={() => {
              cycle("left");
              startAutoAdvance();
            }}
            aria-label="Previous"
          >
            {chevron("left")}
          </button>
          <div className="flex items-center gap-2">
            {cards.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  goToIndex(i);
                  startAutoAdvance();
                }}
                aria-label={`Go to slide ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer p-0 border-0 ${
                  i === centerIndex ? "bg-black/70 dark:bg-white/80 scale-[1.3]" : "bg-black/15 dark:bg-white/15"
                }`}
              />
            ))}
          </div>
          <button
            className={`${ARROW_CLASSES} w-10 h-10 md:w-12 md:h-12`}
            onClick={() => {
              cycle("right");
              startAutoAdvance();
            }}
            aria-label="Next"
          >
            {chevron("right")}
          </button>
        </div>
      )}
    </section>
  );
}
